import React, { useEffect, useState } from "react";
import "./FitToFlyflow.css";
import { useLocation, useParams } from "react-router-dom";
import { useStore } from "../../../../../Hooks/useStore";
import FitToFlyService from "../../../../../services/fitToFly/fitToFly.service";

const FitToFlyView = () => {
  const { rootStore } = useStore();
  const { passengerId } = useParams();
  const location = useLocation();
  const passenger = location.state?.passenger || {};
  const [fitToFlyData, setFitToFlyData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFitToFlyData = async () => {
      try {
        setLoading(true);
        const response = await FitToFlyService.getAssessmentByPassenger(passengerId);
        setFitToFlyData(response);
      } catch (error) {
        console.error("Error loading Fit-to-Fly data:", error);
        rootStore.notificationToastStore.showNotification(
          "Failed to load Fit-to-Fly details.",
          true
        );
      } finally {
        setLoading(false);
      }
    };
    fetchFitToFlyData();
  }, [passengerId]);

  if (loading) {
    return (
      <div className="fit2fly-container">
        <div className="flow-card">
          <h3>Loading Fit-to-Fly details...</h3>
        </div>
      </div>
    );
  }

  if (!fitToFlyData) {
    return (
      <div className="fit2fly-container">
        <div className="flow-card">
          <h3>No Fit-to-Fly record found for this passenger.</h3>
        </div>
      </div>
    );
  }


  const getStatusColor = (status) => {
        switch (status.toUpperCase()) {
          case "CLEARED":
            return "#28a745"; // green
          case "DENIED":
            return "#dc3545"; // red
          case "PENDING":
            return "#ffc107"; // yellow
          case "CLEARED_WITH_CONDITIONS":
            return "#007bff"; // blue
          default:
            return "#6c757d"; // gray
        }
      };

  return (
    <div className="fit2fly-container">
      <div className="flow-card">
        <h2 style={{ display: "flex", alignItems: "center", gap: "10px" }}>Fit-to-Fly Assessment Review
          {fitToFlyData.status && (
              <span
              style={{
                  padding: "4px 10px",
                  borderRadius: "12px",
                  backgroundColor: getStatusColor(fitToFlyData.status),
                  color: "#fff",
                  fontSize: "12px",
                  fontWeight: "bold",
                  textTransform: "uppercase",
              }}
              >
              {fitToFlyData.status}
              </span>
          )}

        </h2>

        <div className="review-section">
          <h3>Passenger Information</h3>
          <ul>
            <li><strong>Name:</strong> {passenger.name}</li>
            <li><strong>Email:</strong> {passenger.email}</li>
            <li><strong>Age:</strong> {passenger.age}</li>
            <li><strong>Gender:</strong> {passenger.gender}</li>
            <li><strong>Organization:</strong> {passenger.orgName}</li>
            <li><strong>Contact:</strong> {passenger.contactNumber}</li>
          </ul>

          <h3>Pre-Screening</h3>
          <ul>
            <li>Recent Surgery: {fitToFlyData.recentSurgery ? "Yes" : "No"}</li>
            <li>Medical Condition: {fitToFlyData.medicalCondition ? "Yes" : "No"}</li>
            <li>Pregnancy: {fitToFlyData.pregnancy ? "Yes" : "No"}</li>
            <li>Special Equipment: {fitToFlyData.specialMedicalEquipment ? "Yes" : "No"}</li>
            <li>Hospitalization (30 Days): {fitToFlyData.hospitalizationInLast30Days ? "Yes" : "No"}</li>
            <li>Oxygen/Escort Needs: {fitToFlyData.needForOxygenOrEscort ? "Yes" : "No"}</li>
          </ul>

          <h3>Medical Assessment</h3>
          {[
            "cardiovascularConditions",
            "respiratoryConditions",
            "postSurgeryInformation",
            "neurologicalConcerns",
            "contagiousDiseases",
          ].map(
            (key) =>
              fitToFlyData[key] && (
                <div key={key} className="medical-note">
                  <strong>
                    {key
                      .replace(/([A-Z])/g, " $1")
                      .replace(/^./, (s) => s.toUpperCase())}
                    :
                  </strong>
                  <p>{fitToFlyData[key]}</p>
                </div>
              )
          )}

          <h3>Vitals</h3>
          <ul>
            <li>Oxygen: {fitToFlyData.oxygenSaturation || "N/A"}</li>
            <li>Heart Rate: {fitToFlyData.heartRate || "N/A"}</li>
            <li>Blood Pressure: {fitToFlyData.bloodPressure || "N/A"}</li>
            <li>
              Shortness of Breath:{" "}
              {fitToFlyData.shortnessOfBreath ? "Yes" : "No"}
            </li>
            <li>
              Neurological Stability:{" "}
              {fitToFlyData.neurologicalStability || "N/A"}
            </li>
          </ul>

          <h3>Equipment</h3>
          <ul>
            <li>
              Supplemental Oxygen:{" "}
              {fitToFlyData.requireSupplementalOxygen ? "Yes" : "No"}
            </li>
            <li>
              Wheelchair/Stretcher:{" "}
              {fitToFlyData.needForWheelchairOrStretcher ? "Yes" : "No"}
            </li>
            <li>
              Medical Escort:{" "}
              {fitToFlyData.needForMedicalEscort ? "Yes" : "No"}
            </li>
            <li>
              Medications In-Flight:{" "}
              {fitToFlyData.medicationsInFlight ? "Yes" : "No"}
            </li>
          </ul>

          <h3>Doctor's Note</h3>
          {fitToFlyData.doctorNotesFilePath ? (
            <a
              href={fitToFlyData.doctorNotesFilePath}
              target="_blank"
              rel="noopener noreferrer"
            >
              View Uploaded Document
            </a>
          ) : (
            <p>No doctor’s note uploaded.</p>
          )}

          <h3>Signature</h3>
          {fitToFlyData.signatureUrl ? (
            <img
              src={fitToFlyData.signatureUrl}
              alt="Signature"
              style={{
                border: "1px solid #ccc",
                width: "300px",
                height: "100px",
              }}
            />
          ) : (
            <p>No signature available.</p>
          )}

          <div className="date-display">
            <label>Date</label>
            <p>{new Date(fitToFlyData.createdAt).toISOString().split("T")[0]}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FitToFlyView;
